import { useCallback, useMemo } from 'react';
import { Select } from 'foundations-components/transitional/components/Select';
import { CommunicatorLogger } from 'calling-usage-tracker/callingTracker';
import { getButtonSource } from 'calling-integration-sdk-support/viewport/constants/changeProviderButtonLocations';
import { Link } from 'foundations-components/transitional/components/Link';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import { HUBSPOT, TWILIO } from 'calling-internal-common/call-provider/constants/ProviderNames';
import { useSelectedProviderContext } from '../context/SelectedProviderContext';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { CallingIcon } from 'foundations-assets/icons/components/CallingIcon';
import { jsx as _jsx } from "react/jsx-runtime";
export const TWILIO_BASED_PROVIDERS = [TWILIO, HUBSPOT];
const getProviderIcon = provider => {
  if (provider.name === HUBSPOT) {
    return /*#__PURE__*/_jsx(IconCircle, {
      backgroundColor: "fill-accent-light-orange-default",
      borderWidth: "0",
      color: "fill-surface-default",
      "data-selenium-test": "hubspot-calling-icon",
      id: "hubspot-calling-icon",
      icon: CallingIcon,
      padding: "50"
    });
  }
  if (provider.iconUrl) {
    return /*#__PURE__*/_jsx("img", {
      src: provider.iconUrl,
      alt: provider.name,
      height: "22px",
      width: "22px",
      loading: "lazy"
    });
  }
  return /*#__PURE__*/_jsx(IconHolder, {
    backgroundColor: "fill-accent-blue-default",
    borderRadius: "200",
    color: "fill-surface-default",
    "data-selenium-test": "default-calling-icon",
    id: "default-calling-icon",
    padding: "50",
    icon: CallingIcon
  });
};
const getProviderValue = provider => {
  return `${provider.name}_${provider.appId || 0}`;
};
const isAnyThirdPartyInstalled = installedCallingSettings => {
  return installedCallingSettings === null || installedCallingSettings === void 0 ? void 0 : installedCallingSettings.some(provider => !TWILIO_BASED_PROVIDERS.includes(provider.name));
};
const ProviderSelect = ({
  installedCallingSettings,
  callIncomingOrActive = false,
  location
}) => {
  const {
    selectedCallProviderData,
    setCallingPreferencesProvider
  } = useSelectedProviderContext();
  const options = useMemo(() => {
    return (installedCallingSettings === null || installedCallingSettings === void 0 ? void 0 : installedCallingSettings.map(provider => ({
      value: getProviderValue(provider),
      text: provider.name,
      avatar: getProviderIcon(provider)
    }))) || [];
  }, [installedCallingSettings]);
  const dropdownFooterContent = useMemo(() => {
    if (!isAnyThirdPartyInstalled(installedCallingSettings)) {
      return /*#__PURE__*/_jsx(Link, {
        external: true,
        href: `/ecosystem/${PortalIdParser.get()}/marketplace/apps/calling`,
        id: "calling-marketplace-link",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "calling-remote-app.providerMenu.footer"
        })
      });
    }
    return null;
  }, [installedCallingSettings]);
  const handleSelectChange = useCallback(event => {
    // If event is a string, it's the value directly
    const selectedProviderName = typeof event === 'string' ? event : event.target.value;
    const [providerName, appId] = selectedProviderName.split('_');
    const selectedProvider = installedCallingSettings === null || installedCallingSettings === void 0 ? void 0 : installedCallingSettings.find(provider => provider.name === providerName && `${provider.appId || 0}` === appId);
    // Log the provider change event
    if (location && selectedCallProviderData !== null && selectedCallProviderData !== void 0 && selectedCallProviderData.name) {
      CommunicatorLogger.log('callingExtensions_interaction', {
        action: 'change provider',
        provider_name: selectedCallProviderData.name,
        source: getButtonSource(location),
        button_location: location
      });
    }
    if (selectedProvider) {
      setCallingPreferencesProvider(selectedProvider);
    }
  }, [installedCallingSettings, setCallingPreferencesProvider, location, selectedCallProviderData === null || selectedCallProviderData === void 0 ? void 0 : selectedCallProviderData.name]);
  if (!installedCallingSettings || installedCallingSettings.length === 0) {
    return null;
  }
  return /*#__PURE__*/_jsx(Select, {
    id: "provider-select",
    "data-test-id": "provider-select",
    value: getProviderValue(selectedCallProviderData),
    onChange: handleSelectChange,
    options: options,
    disabled: callIncomingOrActive,
    buttonUse: "transparent",
    menuWidth: 'auto',
    inlineSize: "auto !important",
    blockSize: "32px",
    searchable: false,
    dropdownFooter: dropdownFooterContent,
    error: false
  });
};
export default ProviderSelect;